import * as React from "react";
import { Alert, Box, Paper, Snackbar, Typography } from "@mui/material";
import { useLocationsAdmin } from "../components/Locations/useLocationsAdmin";
import LocationTreeView from "../components/Locations/LocationTreeView";
import LocationDetailsPanel, { type FormTarget } from "../components/Locations/LocationDetailsPanel";
import LocationDeleteDialog from "../components/Locations/LocationDeleteDialog";

const LocationSettingsPage: React.FC = () => {
    const {
        loading,
        error,
        clearError,
        status,
        clearStatus,
        locations,
        locationTypes,
        tree,
        createLocation,
        updateLocation,
        moveLocation,
        deleteLocation,
    } = useLocationsAdmin();

    const [target, setTarget] = React.useState<FormTarget | null>(null);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);

    const locationTypeNameById = React.useMemo(
        () => new Map(locationTypes.map((t) => [t.id, t.type])),
        [locationTypes]
    );

    const selectedIdForTree = target?.mode === "edit" ? target.id : null;
    const deleteTarget = deleteId != null ? locations.find((l) => l.locationId === deleteId) ?? null : null;

    const handleConfirmDelete = async () => {
        if (deleteId == null) return;
        await deleteLocation(deleteId);
        if (target?.mode === "edit" && target.id === deleteId) setTarget(null);
        setDeleteId(null);
    };

    if (loading) {
        return (
            <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Loading locations…</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4, px: 2 }}>
            <Typography variant="h5" gutterBottom>
                Locations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Browse and edit your location hierarchy. Use the + button to add new location.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "flex-start" }}>
                <Paper variant="outlined" sx={{ p: 2, width: { xs: "100%", md: 420 }, flexShrink: 0 }}>
                    <LocationTreeView
                        tree={tree}
                        locations={locations}
                        locationTypeNameById={locationTypeNameById}
                        selectedId={selectedIdForTree}
                        onSelect={(id) => setTarget({ mode: "edit", id })}
                        onAddRoot={() => setTarget({ mode: "create", parentId: null })}
                        onAddChild={(parentId) => setTarget({ mode: "create", parentId })}
                        onRequestDelete={(id) => setDeleteId(id)}
                        onMove={(id, newParentId) => {
                            void moveLocation(id, newParentId);
                        }}
                    />
                </Paper>

                <Box sx={{ flexGrow: 1, width: "100%", minHeight: 320 }}>
                    <LocationDetailsPanel
                        target={target}
                        locations={locations}
                        locationTypes={locationTypes}
                        onCreate={createLocation}
                        onUpdate={updateLocation}
                        onRequestDelete={(id) => setDeleteId(id)}
                        onCancel={() => setTarget(null)}
                        onSaved={(id) => setTarget({ mode: "edit", id })}
                    />
                </Box>
            </Box>

            <LocationDeleteDialog
                location={deleteTarget}
                locations={locations}
                onCancel={() => setDeleteId(null)}
                onConfirm={handleConfirmDelete}
            />

            <Snackbar
                open={!!status}
                autoHideDuration={3000}
                onClose={clearStatus}
                message={status}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            />
        </Box>
    );
};

export default LocationSettingsPage;
